import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDroppable } from '@dnd-kit/core';

import { Spinner } from '@dopomogai/ui';
import { DraggableTaskCard } from './DraggableTaskCard';
import type { ProjectTask } from '@dopomogai/types';
import { useUiStore } from '../../../../../../apps/website/src/stores/ui'; 

interface TaskBacklogColumnProps {
  tasks: ProjectTask[];
  isLoading?: boolean;
}

/**
 * A dedicated column to display a list of unassigned tasks from the project backlog.
 * It also acts as a droppable area to return tasks to the backlog.
 */
export const TaskBacklogColumn: React.FC<TaskBacklogColumnProps> = ({
  tasks,
  isLoading,
}) => {
  const { t } = useTranslation('admin_projects');
  const { setNodeRef } = useDroppable({ id: 'backlog-drop-zone' });
  const { openTaskInspector } = useUiStore();

  const renderContent = () => {
    if (isLoading) {
        return <div className="p-4 flex justify-center"><Spinner /></div>
    }

    if (tasks.length === 0) {
        return (
            <div className="p-4 text-center text-sm text-slate-500 border-2 border-dashed rounded-xl bg-slate-50">
                <p>{t('backlog.empty')}</p>
            </div>
        );
    }
    
    return (
        <div className="space-y-3">
            {tasks.map(task => (
                <DraggableTaskCard 
                    key={task._id} 
                    task={task} 
                    // --- THIS IS THE FIX (Part 2) ---
                    // Wire up the required onExpand prop. In this context, it opens the full editor modal.
                    onExpand={() => openTaskInspector(task, { 
                        projectId: task.project_id, 
                        sprintId: task.sprint_id ?? undefined 
                    })}
                />
            ))}
        </div>
    );
  }

  return (
    <div className="h-full bg-slate-100 border-l-2 border-black flex flex-col">
      <header className="p-4 border-b-2 border-black flex justify-between items-center flex-shrink-0 bg-white">
        <h3 className="text-xl font-bold text-slate-800">{t('backlog.title')}</h3>
        <span className="font-bold text-slate-500 bg-slate-200 px-2.5 py-1 rounded-full text-sm">
          {tasks.length}
        </span>
      </header>
      <div ref={setNodeRef} className="p-4 overflow-y-auto flex-grow">
        {renderContent()}
      </div>
    </div>
  );
};